import { Inject, Injectable } from '@angular/core';
import {
  CMS_CONFIG,
  CmsConfig,
  CmsFacade,
  formatCmsContent,
  formatCmsSeoData,
} from '@domgen/dgx-fe-cms';
import { filterNullUndefined } from '@domgen/dgx-fe-common';
import { CONFIG } from '@domgen/dgx-fe-config';
import { SeoData, SeoService } from '@domgen/dgx-fe-seo';
import { ComponentStore } from '@ngrx/component-store';
import { CmsSelfFixRoot } from '@self-fix/util-models';
import { Observable, combineLatest } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { AppConfig } from '../../models/config/app-config.type';

export interface ViewModel {
  cmsContent: CmsSelfFixRoot;
  planNumber?: string;
}

export interface ComponentState {
  cmsContent?: CmsSelfFixRoot;
  planNumber?: string;
}

@Injectable({ providedIn: 'root' })
export class AppComponentService extends ComponentStore<ComponentState> {
  private readonly cmsContent$: Observable<CmsSelfFixRoot> = this.select(
    (state: ComponentState) => state.cmsContent
  ).pipe(filterNullUndefined());

  readonly planNumber$ = this.select((state: ComponentState) => {
    return state.planNumber;
  });

  readonly setPlanNumber = this.updater(
    (state, planNumber: string): ComponentState => ({
      ...state,
      planNumber,
    })
  );

  private readonly updateCmsContent = this.updater(
    (state: ComponentState, cmsContent: CmsSelfFixRoot) => ({
      ...state,
      cmsContent,
    })
  )(this.getRootCmsContent());

  private readonly setSeoData = this.effect(
    (cmsContent$: Observable<CmsSelfFixRoot>) =>
      cmsContent$.pipe(
        map((cmsSeoSite: CmsSelfFixRoot) => formatCmsSeoData(cmsSeoSite)),
        tap((seoData: SeoData) => this.seoService.setSeoData(seoData))
      )
  )(this.cmsContent$);

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsContent: this.cmsContent$,
    planNumber: this.planNumber$,
  });

  constructor(
    @Inject(CONFIG) private readonly config: AppConfig,
    @Inject(CMS_CONFIG) private readonly cmsConfig: CmsConfig,
    private readonly cmsFacade: CmsFacade,
    private readonly seoService: SeoService
  ) {
    super({});
  }

  private getRootCmsContent(): Observable<CmsSelfFixRoot> {
    const cmsRootPath = this.config.cms.cmsRootPath as string;

    return this.cmsFacade
      .cmsPageContentByPath<CmsSelfFixRoot>(cmsRootPath)
      .pipe(
        first(),
        map((cmsContent: CmsSelfFixRoot) =>
          formatCmsContent(this.cmsConfig.cmsApi, cmsContent)
        )
      );
  }
}
